import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import PageHeader from '../../../components/PageHeader';
import { Add, Search, Group, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import useDebounce from '../../../hooks/useDebounce';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../hooks/useAuth';
import entityService from '../services/entityService';
import { companyService } from '../../companies';
import EntityModalForm from '../components/EntityModalForm';
import LoadingLogo from '../../../components/LoadingLogo';

const EntityList = () => {
  const { hasPermission } = usePermissions();
  const { isOwner } = useAuth();
  const [entities, setEntities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 2000);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openModal, setOpenModal] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadEntities();
  }, [page, debouncedSearch, selectedCompany]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadEntities = async () => {
    try {
      setLoading(true);
      const response = await entityService.getAll({
        search: debouncedSearch,
        companyId: selectedCompany,
        page,
        limit: 10,
      });
      setEntities(response.data);
      setPagination(response.pagination || {});
    } catch (error) {
      showSnackbar('Error al cargar entidades', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleCreate = () => {
    setSelectedEntity(null);
    setOpenModal(true);
  };

  const handleEdit = (entity) => {
    setSelectedEntity(entity);
    setOpenModal(true);
  };

  const handleDelete = async (entityId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario externo?')) return;
    try {
      await entityService.delete(entityId);
      setEntities(prev => prev.filter(e => e.id !== entityId));
      setPagination(prev => prev ? { ...prev, total: (prev.total || 1) - 1 } : prev);
      showSnackbar('Entidad eliminada exitosamente', 'success');
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al eliminar entidad', 'error');
    }
  };

  const handleSave = async (data) => {
    try {
      if (selectedEntity) {
        await entityService.update(selectedEntity.id, data);
        setEntities(prev => prev.map(e =>
          e.id === selectedEntity.id ? { ...e, ...data } : e
        ));
        showSnackbar('Entidad actualizada exitosamente', 'success');
      } else {
        await entityService.create(data);
        showSnackbar('Entidad creada exitosamente', 'success');
        if (page !== 1) setPage(1);
        else loadEntities();
      }
      setOpenModal(false);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar entidad', 'error');
      throw error;
    }
  };

  const totalPages = pagination ? Math.ceil((pagination.total || 0) / (pagination.limit || 10)) : 1;
  const from = pagination ? ((page - 1) * (pagination.limit || 10)) + 1 : 1;

  return (
    <Box>
      <PageHeader
        title="Usuarios Externos"
        subtitle="Entidades y contactos externos que radican o reciben correspondencia."
        stat={pagination?.total || 0}
        statLabel="Externos"
        actions={hasPermission('entity.create') && (
          <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
            Nueva Entidad
          </Button>
        )}
      />

      <Box sx={{ bgcolor: '#f2f2f3', borderRadius: 0, p: '18px', border: '1px solid rgba(29,31,32,0.16)', mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Buscar</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar entidades..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isOwner && (
            <Box>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(29,31,32,0.55)', mb: 0.5, fontWeight: 500 }}>Empresa</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={selectedCompany}
                  onChange={(e) => { setSelectedCompany(e.target.value); setPage(1); }}
                  displayEmpty
                >
                  <MenuItem value="">Todas</MenuItem>
                  {companies.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <LoadingLogo size={120} />
        </Box>
      ) : entities.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Group sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography>No hay entidades registradas</Typography>
        </Paper>
      ) : (
        <Box>
          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Nombre</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Email</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>Identificacion</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Telefono</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }}>Ciudad</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Empresa</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entities.map((entity) => (
                    <TableRow key={entity.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600 }}>
                          {entity.name}{entity.lastName ? ` ${entity.lastName}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entity.email || '\u2014'}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{entity.dni || '\u2014'}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">{entity.phone || '\u2014'}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2">
                          {[entity.city, entity.state].filter(Boolean).join(', ') || '\u2014'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2">{entity.company?.name || '\u2014'}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                          {hasPermission('entity.update') && (
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => handleEdit(entity)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <EditOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('entity.delete') && (
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={() => handleDelete(entity.id)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <DeleteOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {pagination && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'rgba(29,31,32,0.55)' }}>
                Mostrando {from} de {pagination.total || 0} entidades
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
                  Anterior
                </Button>
                <Button variant="outlined" size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)} sx={{ fontWeight: 600, minWidth: 90 }}>
                  Siguiente
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      <EntityModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        entity={selectedEntity}
        companies={companies}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EntityList;
